// The position's artifact mirror (§31 A6, A9, A11): what movegen must know of
// three artifacts, derived from the industry after every command.
//
// - `Position.wards` — Deflector Shields lit (A9): a Shield Generator whose
//   badge is powered (`gearPowered`) and that was fed its round's points.
//   Whether a capture kills reads them, so they live where movegen looks.
// - `Position.artisans` — sides whose Artisan Pillar is on (A6): max HP,
//   regeneration and the Monarch's step read it.
// - `Position.brass` — bodies bearing powered Knuckles of Brass (A11): the
//   blow reads it.
//
// Derived, never hashed (gear, `lit` and the structures are), replaced
// wholesale; rebuilt by `applyCommand` and on restore.

import { invalidateMoves, type Position, type Ward } from "../position";
import { gearOf, gearPowered } from "./gear";
import { ARTISAN_PILLAR, HOUSE_OF_CARDS, type IndustryState, SHIELD_GENERATOR, structureAt } from "./state";

interface Mirror {
  wards: Ward[];
  artisans: number[];
  brass: number[];
}

/** What the mirror should say for this industry. */
export function artifactMirror(industry: IndustryState): Mirror {
  const wards: Ward[] = [];
  const artisans = new Set<number>();
  const brass: number[] = [];
  for (const gear of gearOf(industry)) {
    if (!gearPowered(industry, gear)) continue;
    if (gear.kind === SHIELD_GENERATOR && structureAt(industry, gear.anchor)?.lit) {
      wards.push({ at: gear.anchor, side: gear.side });
    } else if (gear.kind === ARTISAN_PILLAR) {
      artisans.add(gear.side);
    } else if (gear.kind === HOUSE_OF_CARDS && gear.holder > 0) {
      brass.push(gear.holder);
    }
  }
  wards.sort((a, b) => a.at - b.at || a.side - b.side);
  return { wards, artisans: [...artisans].sort((a, b) => a - b), brass: brass.sort((a, b) => a - b) };
}

const sameWards = (a: readonly Ward[], b: readonly Ward[]): boolean =>
  a.length === b.length && a.every((w, i) => w.at === b[i].at && w.side === b[i].side);
const sameList = (a: readonly number[], b: readonly number[]): boolean =>
  a.length === b.length && a.every((s, i) => s === b[i]);

/** Does the position's mirror disagree with the industry? Cheap when nothing is in play. */
export function artifactMirrorStale(position: Position, industry: IndustryState): boolean {
  if (!industry.gear && position.wards.length === 0 && position.artisans.length === 0 && position.brass.length === 0) {
    return false;
  }
  const want = artifactMirror(industry);
  return (
    !sameWards(position.wards, want.wards) ||
    !sameList(position.artisans, want.artisans) ||
    !sameList(position.brass, want.brass)
  );
}

/** Bring the mirror up to date in place. */
export function syncArtifactMirror(position: Position, industry: IndustryState): void {
  const want = artifactMirror(industry);
  invalidateMoves(position);
  position.wards = want.wards;
  position.artisans = want.artisans;
  position.brass = want.brass;
}
